import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, addDays } from 'date-fns';
import { X, Calendar, Clock } from 'lucide-react';
import TextWithLinks from '../Shared/TextWithLinks';

export default function ScheduleActivityModal({ activity, onClose }) {
  const { scheduleToCalendar } = useFamilyContext();
  const [selectedDate, setSelectedDate] = useState(activity.prefillDate || format(new Date(), 'yyyy-MM-dd'));
  const [selectedTime, setSelectedTime] = useState(activity.prefillTime || '10:00');

  const handleSchedule = (e) => {
    e.preventDefault();
    scheduleToCalendar(activity, selectedDate, selectedTime);
    onClose();
  };

  // Generate next 14 days for date selection
  const dateOptions = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'EEEE, MMMM d'),
      isToday: i === 0
    };
  });

  const timeOptions = [
    '8:00', '9:00', '10:00', '11:00', '12:00', 
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-stone-900">Schedule Activity</h2>
            <p className="text-sm text-stone-600 mt-1">Add "{activity.title}" to family calendar</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <form onSubmit={handleSchedule} className="p-6 space-y-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-stone-700 mb-3">
              <Calendar className="w-4 h-4 inline mr-2" />
              Select Date
            </label>
            <select
              id="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            >
              {dateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.isToday && '(Today)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="time" className="block text-sm font-medium text-stone-700 mb-3">
              <Clock className="w-4 h-4 inline mr-2" />
              Select Time
            </label>
            <select
              id="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            >
              {timeOptions.map(time => (
                <option key={time} value={time}>
                  {time}:00
                </option>
              ))}
            </select>
          </div>

          <div className="bg-teal_violet-50 p-4 rounded-lg">
            <h4 className="font-medium text-stone-900 mb-2">Activity Summary</h4>
            <p className="text-sm text-stone-700 mb-1"><strong>Title:</strong> {activity.title}</p>
            <p className="text-sm text-stone-700"><strong>Description:</strong> <TextWithLinks text={activity.description} /></p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gold-600 text-white font-medium rounded-lg hover:bg-gold-700 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Add to Calendar
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-stone-200 text-stone-700 font-medium rounded-lg hover:bg-stone-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}